import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RootState } from 'redux/store';
import { CallArgs, IResponse } from '../types';
import { toast } from 'utils/toast';
import { setRooms, setUserEmail } from 'pages/app/redux';
import { setBranchFiles, setLogs } from 'pages/Logs/redux';
import { OrganizationData, setOrganizationData } from 'pages/redux';
import { useState } from 'react';
import { usePath } from 'pages/hooks';
import { DiscreteAccess, emptyDiscreteAccess, setUsers } from 'pages/Settings/redux';
import { clearLiveDemoExitToTry, getApiUrl, markLiveDemoExitToTry } from 'utils';
import { resetChatwootContact } from 'utils/chatwootBubble';

const readStoredDiscreteAccess = (): DiscreteAccess => {
  try {
    const raw = localStorage.getItem('discreteAccess');
    if (!raw) return emptyDiscreteAccess();
    const parsed = JSON.parse(raw);
    return {
      inventoryEdit: Boolean(parsed?.inventoryEdit),
      departmentsEdit: Boolean(parsed?.departmentsEdit),
      crmEdit: Boolean(parsed?.crmEdit),
    };
  } catch {
    return emptyDiscreteAccess();
  }
};

export interface GlobalState {
  token: string;
  permission: number | null;
  username: string | null,
  /** Staff row id from auth / staff record. */
  staffId: string | null,
  discreteAccess: DiscreteAccess,
  /** Marketing live demo session — BE rejects writes. */
  liveView: boolean,
}

const initialState: GlobalState = {
  token: localStorage.getItem('token') || '',
  permission: localStorage.getItem('permission') === '0' ? 0 : Number(localStorage.getItem('permission')) || null,
  username: localStorage.getItem('username') || '',
  staffId: localStorage.getItem('staffId') || '',
  discreteAccess: readStoredDiscreteAccess(),
  liveView: localStorage.getItem('liveView') === '1',
};

export const counterSlice = createSlice({
  name: 'global',
  initialState,
  reducers: {
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
      localStorage.setItem('token', action.payload);
    },
    setUsername: (state, action: PayloadAction<string>) => {
      state.username = action.payload;
      localStorage.setItem('username', action.payload);
    },
    setStaffId: (state, action: PayloadAction<string>) => {
      state.staffId = action.payload;
      if (action.payload) {
        localStorage.setItem('staffId', action.payload);
      } else {
        localStorage.removeItem('staffId');
      }
    },
    setPermission: (state, action: PayloadAction<number | null>) => {
      state.permission = action.payload;
      if (action.payload?.toString()) {
        localStorage.setItem('permission', action.payload.toString());
      } else {
        localStorage.removeItem('permission');
      }
    },
    setLiveView: (state, action: PayloadAction<boolean>) => {
      state.liveView = Boolean(action.payload);
      if (action.payload) {
        localStorage.setItem('liveView', '1');
        // Keep exit flag after logout clears liveView — SignIn/settings send demos to /try.
        markLiveDemoExitToTry();
      } else {
        localStorage.removeItem('liveView');
      }
    },
    setDiscreteAccess: (state, action: PayloadAction<DiscreteAccess | null | undefined>) => {
      const next = {
        inventoryEdit: Boolean(action.payload?.inventoryEdit),
        departmentsEdit: Boolean(action.payload?.departmentsEdit),
        crmEdit: Boolean(action.payload?.crmEdit),
      };
      state.discreteAccess = next;
      localStorage.setItem('discreteAccess', JSON.stringify(next));
    },
  },
});

export const {
  setToken, setUsername, setStaffId, setPermission, setDiscreteAccess, setLiveView,
} = counterSlice.actions;

export const selectToken = (state: RootState) => state.global.token;
export const selectPermission = (state: RootState) => state.global.permission;
export const selectUsername = (state: RootState) => state.global.username;
export const selectStaffId = (state: RootState) => state.global.staffId;
export const selectDiscreteAccess = (state: RootState) => state.global.discreteAccess;
export const selectLiveView = (state: RootState) => state.global.liveView;

export default counterSlice.reducer;
// type IDispatch = (action: any) => {};

export const callEndpoint = ({
  api, body, type = 'json', method = 'GET', contentType, noStringify, noContentType, checkLimit, signal
}: CallArgs) => async (dispatch: Function, getState: Function): Promise<IResponse> => {
  const state = getState();
  const { token } = state.global;
  const { qtConfig } = state.app;
  // eslint-disable-next-line prefer-destructuring
  const organizationData: OrganizationData | undefined = state.views.organizationData;

  const organizationDataHeader = {
    hDId: organizationData?.id,
    hDName: organizationData?.name,
    hDAddress: organizationData?.address,
    hDEmail: organizationData?.email,
    // Below '' needed as you cant pass null/undefined in header => it will be interpreted as string
    hDLinkedin: organizationData?.linkedin || '',
    hDFacebook: organizationData?.facebook || '',
    hDInstagram: organizationData?.instagram || '',
    hDTwitter: organizationData?.twitter || '',
    hDPhoneNumber: organizationData?.phoneNumber || '',
    hDLogo: organizationData?.logo || '',
    hDCurrency: encodeURIComponent(organizationData?.currency || '') || '$',
    hDCoId: organizationData?.coId || '',
    hDSuffix: organizationData?.suffix || '',
    hDBranch: organizationData?.branch || ''
  };

  const options: any = {
    method,
    headers: {
      'Content-Type': contentType ?? 'application/json',
      token,
      ...organizationDataHeader,
      checkLimit,
    },
    body: !noStringify ? JSON.stringify({ ...body, ...(qtConfig || {}) }) : body,
    signal,
  };

  if (noContentType) delete options.headers['Content-Type'];
  if (method.toUpperCase() === 'GET') delete options.body;

  try {
    const url = window.location.hostname === (process.env.REACT_APP_MOBILE_APP_URL || '').split(':8200')
      .join('').split('http://').join('').split('/').join('') ?
      process.env.REACT_APP_MOBILE_APP_URL : getApiUrl();

    const response = await fetch(
      `${url}${api}`,
      options,
    );

    if (!response) {
      return { status: 'error', data: 'Internet connection is not detected' } as IResponse;
    }

    let dataFromEndPoint: any = JSON.stringify(
      { status: 'error', data: 'Error connecting with server. Please try again' }
    );
    if (type === 'json') {
      const dataFromEndPoint1 = await response.json();
      dataFromEndPoint = dataFromEndPoint1 || dataFromEndPoint;
    }
    if (typeof dataFromEndPoint === 'string') {
      return JSON.parse(dataFromEndPoint) as IResponse;
    }
    return dataFromEndPoint as IResponse;
  } catch (e: any) {
    if (e?.name === 'AbortError' || signal?.aborted) {
      return { status: 'error', data: 'CANCELLED' } as IResponse;
    }
    const message = e.message.toLowerCase() === 'failed to fetch' ?
      'Please check your connection.' : e.message;
    return { status: 'error', data: message } as IResponse;
  }
};

type ClientsBody = {
  email: string
  password: string
  /** App tenant id. Sent to auth as organizationId (API/DB name). */
  organizationId?: string
}

/** Auth result — pick org only when the seat spans multiple organizations. */
export type LoginResult =
  | { kind: 'ok', organizationId: string }
  | { kind: 'pick', organizations: { id: string, name: string }[] }
  | { kind: 'error' }

export const refreshToken = () => async (dispatch: Function): Promise<IResponse> => {
  const response: IResponse = await dispatch(
    callEndpoint({ api: 'refresh', method: 'GET' }),
  );
  return response;
};

export const useCreds = () => {
  const [expireCredsTimeout, setExpireCredsTimeout] = useState<any>();
  const { navigateToHome } = usePath();

  const logout = (isDontLogOutTabs?: true) => async (dispatch: Function): Promise<void> => {
    dispatch(setToken(''));
    dispatch(setPermission(null));
    dispatch(setUsername(''));
    dispatch(setStaffId(''));
    dispatch(setDiscreteAccess(emptyDiscreteAccess()));
    dispatch(setLiveView(false));
    dispatch(setRooms(null));
    dispatch(setLogs(null));
    dispatch(setBranchFiles(null));
    dispatch(setOrganizationData(null));
    dispatch(setBranchFiles(null));
    localStorage.removeItem('discreteAccess');
    dispatch(setUserEmail(''));
    dispatch(setUsers(null));
    resetChatwootContact();
    clearTimeout(expireCredsTimeout);
    setExpireCredsTimeout(null);

    // Session mock room-charge settles + live banner dismiss die with the sign-in
    try {
      sessionStorage.removeItem('session_dismissed_room_charges');
      sessionStorage.removeItem('session_dismissed_live_view_banner');
      localStorage.removeItem('live_dismissed_room_charges');
    } catch {
      /* ignore */
    }

    if (!isDontLogOutTabs) localStorage.setItem('logout_time', new Date().toISOString());
  };

  const expireCreds = (expiryTime?: number) => async (dispatch: Function): Promise<void> => {
    let timeout = (60000 * 10) - 60000;
    if (expiryTime) {
      timeout = (new Date(expiryTime * 1000)).getTime() - new Date().getTime();
      if (timeout <= 60000) {
        timeout = 1;
      } else {
        timeout -= 60000;
      }
    }
    clearTimeout(expireCredsTimeout);
    setExpireCredsTimeout(null);
    setExpireCredsTimeout(setTimeout(async () => {
      const res = await dispatch(refreshToken());
      if (res.status === 'success') {
        dispatch(setToken(res.data));
        dispatch(expireCreds());
        return;
      }
      // Retry once after a short delay for transient/network errors
      await new Promise<void>((resolve) => { setTimeout(resolve, 5000); });
      const retryRes = await dispatch(refreshToken());
      if (retryRes.status === 'success') {
        dispatch(setToken(retryRes.data));
        dispatch(expireCreds());
        return;
      }
      toast(retryRes.data || 'Authorization failed', { type: 'error' });
      dispatch(logout());
    }, timeout));
  };

  const otherTabLogin = () => (dispatch: Function): void => {
    dispatch(setToken(localStorage.getItem('token') || ''));
    const permStorage = localStorage.getItem('permission');
    const permission = permStorage ? +permStorage : null;
    dispatch(setPermission(permission));
    dispatch(setUsername(localStorage.getItem('username') || ''));
    dispatch(setStaffId(localStorage.getItem('staffId') || ''));
    dispatch(setDiscreteAccess(readStoredDiscreteAccess()));
    dispatch(setLiveView(localStorage.getItem('liveView') === '1'));
    dispatch(expireCreds());
    navigateToHome(permission);
  };

  const login = (body: ClientsBody) => async (dispatch: Function): Promise<LoginResult> => {
    // Cloud Run cold starts can drop the CORS preflight (no ACAO) — retry transient network failures.
    const isTransientNetworkError = (res: IResponse) => {
      const msg = String(res?.data || '').toLowerCase();
      return msg.includes('check your connection')
        || msg.includes('failed to fetch')
        || msg.includes('networkerror')
        || msg.includes('error connecting with server');
    };

    let res: IResponse = { status: 'error', data: 'Please check your connection.' } as IResponse;
    const maxAttempts = 3;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      res = await dispatch(
        callEndpoint({
          api: 'auth',
          body: {
            email: body.email,
            password: body.password,
            organizationId: body.organizationId,
          },
          method: 'POST',
        }),
      );
      if (res.status === 'success' || !isTransientNetworkError(res)) break;
      if (attempt < maxAttempts) {
        await new Promise<void>((resolve) => { setTimeout(resolve, 800 * attempt); });
      }
    }
    if (res.status !== 'success') {
      toast(res.data, { type: 'error' });
      return { kind: 'error' };
    }
    // Multi-org: stop here — client picks, then calls login again with organizationId.
    if (res.data?.needsOrganizationPick) {
      return {
        kind: 'pick',
        organizations: Array.isArray(res.data.organizations) ? res.data.organizations : [],
      };
    }
    dispatch(setToken(res.data.token));
    dispatch(setPermission(Number(res.data.permission)));
    dispatch(setUsername(res.data.username));
    dispatch(setStaffId(res.data.staffId != null ? String(res.data.staffId) : ''));
    dispatch(setDiscreteAccess(res.data.discreteAccess || emptyDiscreteAccess()));
    dispatch(setLiveView(Boolean(res.data.liveView)));
    // Real staff login — do not keep sending this tab to marketing /try.
    if (!res.data.liveView) clearLiveDemoExitToTry();
      dispatch(expireCreds());
      localStorage.setItem('organization_data_id', res.data.organizationId.toString());
      localStorage.setItem('login_time', new Date().toISOString());
      localStorage.removeItem('logout_time');
      return { kind: 'ok', organizationId: res.data.organizationId.toString() };
  };

  return {
    expireCreds, logout, login, otherTabLogin
  };
};
